'use client';

import { motion } from 'framer-motion';
import type { Campaign } from '@/lib/types';
import { TIMING } from '@/lib/constants';
import { sanitizeHTML } from '@/lib/sanitize';
import { resolveCompletionCopy } from '@/lib/completion-copy';

const stagger = {
  hidden: { opacity: 0 },
  visible: (i: number) => ({
    opacity: 1,
    transition: { delay: i * (TIMING.STAGGER_DELAY / 1000), duration: 0.4, ease: 'easeOut' as const },
  }),
};

interface CompletedScreenProps {
  campaign?: Campaign | null;
  completionContext?: 'just_completed' | 'already_completed' | null;
}

export function CompletedScreen({ campaign, completionContext }: CompletedScreenProps) {
  const { headline, body } = resolveCompletionCopy(campaign, completionContext);

  return (
    <div
      data-testid="completion-screen"
      className="flex flex-col items-center justify-center px-6 py-12 text-center"
    >
      {/* Checkmark */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-accent/10"
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          stroke="var(--accent)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <motion.path
            d="M6 16.5l7 7L26 9"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          />
        </svg>
      </motion.div>

      <motion.div
        custom={1}
        initial="hidden"
        animate="visible"
        variants={stagger}
        role="heading"
        aria-level={1}
        data-testid="completion-headline"
        className="font-heading text-[20px] font-normal tracking-[0.01em] text-text-primary [&_p]:m-0"
        data-ph-no-capture
        dangerouslySetInnerHTML={{ __html: sanitizeHTML(headline) }}
      />

      {body ? (
        <motion.div
          custom={2}
          initial="hidden"
          animate="visible"
          variants={stagger}
          data-testid="completion-body"
          data-ph-no-capture
          className="mt-4 max-w-sm font-body text-base leading-[1.65] text-text-secondary [&_p]:m-0"
          dangerouslySetInnerHTML={{ __html: sanitizeHTML(body) }}
        />
      ) : null}
    </div>
  );
}
